import {type} from 'arktype';
import {OperationsSettings} from './OperationsSettings';
import {
    AccountId,
    CurrencyCode,
    DoubleString,
    NonNegativeInt,
    PaymentCardId,
    PaymentNetworkId,
    ProviderId,
    Source,
} from './shared';
type Provider = typeof Provider.infer;
const Provider = type({
    id: ProviderId,
    name: 'string',
});

type Account = typeof Account.infer;
const Account = type({
    id: AccountId,
    type: "'fiat' | 'crypto' | 'stablecoin'",
    icon: Source,
    providerId: ProviderId.or('null'),
    balance: DoubleString,
    balanceIn: DoubleString,
    balanceOut: DoubleString,
    currencyName: 'string',
    currencyCode: CurrencyCode,
    currencyDecimals: NonNegativeInt,
});

export type Flag = typeof Flag.infer;
export const Flag = type({
    level: "'problem' | 'warning'",
    reasonCode: type.never
        .or("'ADDRESS_UNVERIFIED'")
        .or("'ADDRESS_PENDING'")
        .or("'IBAN_PENDING'")
        .or("'CARD_UNVERIFIED'")
        .or("'CARD_PENDING'"),
});

type PaymentCard = typeof PaymentCard.infer;
const PaymentCard = type({
    id: PaymentCardId,
    type: "'card'",
    name: 'string > 0',
    flags: Flag.array(),
    icon: Source,
    numberFormatted: 'string > 0',
    holderFullName: 'string > 0',
    expirationDate: 'string',
});

export const PaymentNetworkType = type("'sepa' | 'swift' | 'fedwire' | 'ach' | 'nspk' | 'npck'");
export type PaymentNetworkType = typeof PaymentNetworkType.infer;

type PaymentNetwork = typeof PaymentNetwork.infer;
const PaymentNetwork = type({
    id: PaymentNetworkId,
    type: "'payment_network'",
    method: PaymentNetworkType,
    name: 'string > 0',
    icon: Source,
    flags: Flag.array(),
    description: 'string?',
});

export type TopupRequisiteEntriesKey = 'beneficiaryName' | 'iban' | 'reference' | 'beneficiaryAddress' | 'bic';

export type TargetAccountRef = typeof TargetAccountRef.infer;
const TargetAccountRef = type({
    id: Account.get('id'),
    type: Account.get('type'),
});

type TargetWallet = typeof TargetWallet.infer;
const TargetWallet = type({
    id: 'string | number',
    type: "'wallet'",
});

type Target = typeof Target.infer;
const Target = Account.or(PaymentCard).or(PaymentNetwork).or(TargetWallet);

export type TargetRef = typeof TargetRef.infer;
const TargetRef = type({
    id: Target.get('id'),
    type: Target.get('type'),
});

type Operation = typeof Operation.infer;
const Operation = type({
    type: "'withdraw' | 'topup' | 'exchange'",
    accounts: TargetAccountRef.array(),
    targets: TargetRef.array(),
});

export type OperationsSchemaV2 = typeof OperationsSchemaV2.infer;
export const OperationsSchemaV2 = type({
    providers: Provider.array(),
    operations: Operation.array(),
    accounts: Account.array(),
    paymentCards: PaymentCard.array(),
    paymentNetworks: PaymentNetwork.array(),
    operationsSettings: OperationsSettings,
});
